import {
  Component,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import {
  DEFAULT_DISTRICT_BRANDING_COLORS,
  DistrictBrandingAPIResponse,
  DistrictBrandingPayload,
} from 'src/app/common/dtos/district.dto';
import { AuthService } from 'src/app/common/services/auth/auth.service';
import { EnvironmentService } from 'src/app/common/services/environment/environment.service';
import { UserRole } from 'src/app/common/state/user/role/user-role';
import { User } from 'src/app/common/state/user/user.model';
import { UserState } from 'src/app/common/state/user/user.state';
import { SidebarRoute } from 'src/app/common/types/routes';
import {
  canAccessByFeatureFlag,
  canAccessByRole,
  checkRouteEnvironment,
} from 'src/app/common/utilities/check-route';
import { select } from 'src/app/common/utilities/ngxs-utils';
import { sidebarRoutes } from '../../routes/sidebar.routes';
import { ImpersonateModalComponent } from '../../shared/components/modals/impersonate-modal/impersonate.modal.component';
import { DistrictBrandingService } from '../../shared/services/district-branding/district-branding.service';
import { UnleashService } from '../../shared/services/unleash/unleash.service';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  standalone: false,
})
export class SidebarComponent implements OnInit {
  @Output() readonly linkClicked: EventEmitter<unknown> = new EventEmitter();

  @ViewChild('impersonateModal') impersonateModal: ImpersonateModalComponent;

  isImpersonatingUser = false;

  Roles = UserRole;

  user$: Observable<User | null> = select(UserState.getUser);

  user: User;

  sidebarItems = sidebarRoutes;

  planEnabled = false;

  branding: DistrictBrandingPayload | null = null;

  constructor(
    private store: Store,
    private authService: AuthService,
    private featureFlagService: UnleashService,
    private districtBrandingService: DistrictBrandingService
  ) {
    this.isImpersonatingUser = this.store.selectSnapshot(
      (state) => state.user.impersonated
    );
  }

  get sidebarDistrictLogoSrc(): string {
    if (this.branding?.logo) {
      return `${EnvironmentService.apiUrl()}${this.branding.logo}`;
    }

    return '';
  }

  ngOnInit(): void {
    this.user$.subscribe((res: User | null) => {
      if (res) {
        this.user = res;
        if (res.district?.id) {
          this.loadDistrictBranding(res.district.id);
        }
      }
    });

    this.districtBrandingService.currentBranding$.subscribe(
      (branding: DistrictBrandingPayload | null) => {
        if (branding) {
          this.branding = branding;
        }
      }
    );
  }

  get sidebarBrandingStyles(): { [key: string]: string } {
    if (!this.branding) {
      return {};
    }

    return {
      '--sidebar-bg-color': this.branding.bg_color,
      '--sidebar-link-color': this.branding.link_color,
      '--sidebar-active-bg-color': this.branding.active_bg_color,
      '--sidebar-active-link-color': this.branding.active_link_color,
    };
  }

  private loadDistrictBranding(districtId: number): void {
    this.districtBrandingService.getDistrictBranding(districtId).subscribe({
      next: (response: { item: DistrictBrandingAPIResponse }) => {
        if (response?.item) {
          const branding: DistrictBrandingPayload = {
            district_id: response.item.district_id,
            logo: response.item.logo,
            bg_color: response.item.bg_color,
            active_bg_color: response.item.active_bg_color,
            link_color: response.item.link_color,
            active_link_color: response.item.active_link_color,
          };
          this.districtBrandingService.setCurrentBranding(branding);
        } else {
          this.setDefaultBranding(districtId);
        }
      },
      error: (error: { status?: number }) => {
        if (error && error.status === 404) {
          this.setDefaultBranding(districtId);
        } else {
          // eslint-disable-next-line no-console
          console.error(error);
        }
      },
    });
  }

  private setDefaultBranding(districtId: number): void {
    const branding: DistrictBrandingPayload = {
      district_id: districtId,
      logo: '',
      ...DEFAULT_DISTRICT_BRANDING_COLORS,
    };

    this.districtBrandingService.setCurrentBranding(branding);
  }

  linkSelected() {
    this.linkClicked.emit();
  }

  unimpersonate() {
    this.authService.stopImpersonatingUser();
  }

  shouldShowRoute = (route: SidebarRoute): boolean => {
    if (!checkRouteEnvironment(route.path || '')) {
      return false;
    }
    if (route.data.featureFlags) {
      return canAccessByFeatureFlag(route, this.featureFlagService);
    }
    return canAccessByRole(route, this.user);
  };

  logout() {
    this.authService.logoutAllAuthenticatedUsers();
  }
}
